/**
 * หน้าตั้งค่าระบบสำหรับผู้ดูแลระบบ
 *
 * แก้ค่าที่ผู้ว่าจ้างยังไม่ยืนยันได้จากที่นี่โดยไม่ต้อง deploy ใหม่
 * ทุกการเปลี่ยนแปลงถูกบันทึกลง audit log พร้อมค่าก่อนและหลัง
 */
import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '../lib/api';
import type { AppSettings } from '../../shared/config';

/**
 * ร่างการแก้ไข — partial สองชั้น
 * ชั้นนอกคือ "กลุ่มไหนถูกแตะบ้าง" ชั้นในคือ "field ไหนในกลุ่มนั้นถูกแก้"
 * ตรงกับที่ PATCH /api/admin/settings รับ คือส่งมาเฉพาะ field ที่เปลี่ยนจริง
 */
type SettingsDraft = { [K in keyof AppSettings]?: Partial<AppSettings[K]> };

export function AdminRoute() {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [draft, setDraft] = useState<SettingsDraft>({});
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    apiFetch<{ settings: AppSettings }>('/api/admin/settings', { signal: controller.signal })
      .then((response) => setSettings(response.settings))
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setError(caught instanceof ApiError ? caught.message : 'โหลดการตั้งค่าไม่สำเร็จ');
      });
    return () => controller.abort();
  }, []);

  const save = async () => {
    if (Object.keys(draft).length === 0) return;
    setSaving(true);
    setStatus(null);
    setError(null);
    try {
      const response = await apiFetch<{ settings: AppSettings; changed: string[] }>(
        '/api/admin/settings',
        { method: 'PATCH', body: draft },
      );
      setSettings(response.settings);
      setDraft({});
      setStatus(`บันทึกแล้ว: ${response.changed.join(', ')}`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <div className="wrap-wide">
        {error ? (
          <div className="banner banner-bad" role="alert">
            <span>{error}</span>
          </div>
        ) : (
          <div className="empty">
            <span className="spinner" style={{ margin: '0 auto' }} />
          </div>
        )}
      </div>
    );
  }

  const gps = { ...settings.gps, ...draft.gps };
  const image = { ...settings.image, ...draft.image };
  const capture = { ...settings.capture, ...draft.capture };
  const registration = { ...settings.registration, ...draft.registration };

  return (
    <div className="wrap-wide stack-lg">
      <h1>ตั้งค่าระบบ</h1>

      <div className="banner banner-info">
        <span>
          ค่าเหล่านี้มีผลกับทุกเครื่องทันทีหลังบันทึก การเปลี่ยนแปลงถูกบันทึกลง audit log
          ค่าที่ผู้ว่าจ้างยังไม่ยืนยันถูกตั้งเป็นค่าเริ่มต้นที่ปลอดภัยไว้ก่อน
        </span>
      </div>

      {status && (
        <div className="banner banner-ok" role="status">
          <span>{status}</span>
        </div>
      )}
      {error && (
        <div className="banner banner-bad" role="alert">
          <span>{error}</span>
        </div>
      )}

      <section className="card stack">
        <h2>เกณฑ์ GPS</h2>
        <div className="perf" />

        <div className="field">
          <label className="label" htmlFor="threshold">
            ความแม่นยำสูงสุดที่ยอมรับได้ (เมตร)
          </label>
          <select
            id="threshold"
            className="input"
            value={gps.thresholdM}
            onChange={(event) =>
              setDraft({ ...draft, gps: { ...draft.gps, thresholdM: Number(event.target.value) } })
            }
          >
            {gps.thresholdOptionsM.map((option) => (
              <option key={option} value={option}>
                {option} เมตร
              </option>
            ))}
          </select>
          <p className="hint">ค่านี้ถูกบันทึกติดไปกับทุกรายการ เพื่อให้ตรวจย้อนหลังได้</p>
        </div>

        <NumberField
          id="maxWait"
          label="เวลารอสัญญาณสูงสุด (วินาที)"
          value={gps.maxWaitSeconds}
          min={5}
          max={600}
          onChange={(value) => setDraft({ ...draft, gps: { ...draft.gps, maxWaitSeconds: value } })}
        />

        <NumberField
          id="maxAge"
          label="พิกัดเก่าได้ไม่เกิน (วินาที)"
          value={gps.maxPositionAgeSeconds}
          min={5}
          max={3600}
          onChange={(value) =>
            setDraft({ ...draft, gps: { ...draft.gps, maxPositionAgeSeconds: value } })
          }
        />

        <label className="row">
          <input
            type="checkbox"
            checked={gps.allowSupervisorOverride}
            onChange={(event) =>
              setDraft({
                ...draft,
                gps: { ...draft.gps, allowSupervisorOverride: event.target.checked },
              })
            }
          />
          <span>อนุญาตให้ขออนุมัติจากหัวหน้างานเมื่อพิกัดไม่ผ่านเกณฑ์</span>
        </label>
      </section>

      <section className="card stack">
        <h2>รูปภาพ</h2>
        <div className="perf" />

        <NumberField
          id="longEdge"
          label="ด้านยาวสูงสุดหลังย่อ (พิกเซล)"
          value={image.maxLongEdgePx}
          min={320}
          max={4096}
          onChange={(value) =>
            setDraft({ ...draft, image: { ...draft.image, maxLongEdgePx: value } })
          }
        />

        <NumberField
          id="maxPer"
          label="จำนวนรูปต่อหนึ่งรายการ"
          value={image.maxPerCapture}
          min={1}
          max={10}
          onChange={(value) =>
            setDraft({ ...draft, image: { ...draft.image, maxPerCapture: value } })
          }
        />
        <p className="hint">
          ผู้ว่าจ้างยังไม่ยืนยันจำนวนรูปและสิ่งที่ต้องเห็นในภาพ — ค่าเริ่มต้นคือ 1 รูป
        </p>
      </section>

      <section className="card stack">
        <h2>เลขทะเบียนลูกค้า</h2>
        <div className="perf" />

        <div className="field">
          <label className="label" htmlFor="pattern">
            รูปแบบที่บังคับ (regular expression)
          </label>
          <input
            id="pattern"
            className="input mono"
            value={registration.pattern ?? ''}
            placeholder="เว้นว่าง = ไม่ตรวจรูปแบบเพิ่ม"
            onChange={(event) =>
              setDraft({
                ...draft,
                registration: {
                  ...draft.registration,
                  pattern: event.target.value.trim() === '' ? null : event.target.value,
                },
              })
            }
          />
          <p className="hint">
            ตรวจกับค่าหลัง normalize แล้ว เช่น <code>^[0-9]{'{13}'}$</code> สำหรับตัวเลข 13 หลัก ·
            ยังรอผู้ว่าจ้างยืนยันรูปแบบจริงและ check digit
          </p>
        </div>

        <label className="row">
          <input
            type="checkbox"
            checked={registration.allowDuplicateActive}
            onChange={(event) =>
              setDraft({
                ...draft,
                registration: {
                  ...draft.registration,
                  allowDuplicateActive: event.target.checked,
                },
              })
            }
          />
          <span>อนุญาตให้เลขทะเบียนเดียวมีรายการที่ใช้งานอยู่ได้มากกว่าหนึ่งรายการ</span>
        </label>
      </section>

      <section className="card stack">
        <h2>การแก้ไขและฉบับร่าง</h2>
        <div className="perf" />

        <NumberField
          id="editWindow"
          label="เจ้าหน้าที่แก้ไขรายการของตนได้ภายใน (นาที)"
          value={capture.ownerEditWindowMinutes}
          min={0}
          max={10080}
          onChange={(value) =>
            setDraft({ ...draft, capture: { ...draft.capture, ownerEditWindowMinutes: value } })
          }
        />

        <NumberField
          id="draftTtl"
          label="ลบฉบับร่างที่ค้างเกิน (นาที)"
          value={capture.draftTtlMinutes}
          min={15}
          max={10080}
          onChange={(value) =>
            setDraft({ ...draft, capture: { ...draft.capture, draftTtlMinutes: value } })
          }
        />
      </section>

      <div className="actionbar">
        <button
          type="button"
          className="btn btn-block"
          disabled={saving || Object.keys(draft).length === 0}
          onClick={() => void save()}
        >
          {saving ? 'กำลังบันทึก…' : 'บันทึกการตั้งค่า'}
        </button>
        {Object.keys(draft).length > 0 && (
          <button
            type="button"
            className="btn btn-secondary btn-block"
            onClick={() => setDraft({})}
          >
            ยกเลิกการแก้ไข
          </button>
        )}
      </div>
    </div>
  );
}

function NumberField({
  id,
  label,
  value,
  min,
  max,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="field">
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="input mono"
        type="number"
        inputMode="numeric"
        value={value}
        min={min}
        max={max}
        onChange={(event) => {
          const parsed = Number(event.target.value);
          if (Number.isFinite(parsed)) onChange(parsed);
        }}
      />
    </div>
  );
}
